import React from 'react'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import { isKambiSportsSelection } from '@mobi/betslip/helpers/typeGuards'
import type { KambiSportsSelection } from '@mobi/betslip/types'
import { AnimatedHeightToggle } from '@mobi/component-library/Misc/AnimatedHeightToggle'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { observeImmutable } from '@core/Components/HOCs'
import { RewardSelection } from './Components/RewardSelection'
import { SelectedReward } from './Components/SelectedReward'
import { useGetKambiRewardsForSelection } from './hooks/useGetKambiRewardsForSelection'

export const KambiRewardContainer: React.ComponentClass = observeImmutable(
  quickbetState$,
  ({ record }) => {
    const quickbetState = record.toJS() as QuickbetState
    if (!isKambiSportsSelection(quickbetState.selection)) return null

    return (
      <KambiRewardContainerComponent
        canConfirmBet={quickbetState.canConfirmBet}
        selection={quickbetState.selection}
        selectedReward={quickbetState.selectedKambiReward}
      />
    )
  }
)

// ===============
// Local Component
// ===============

const KambiRewardContainerComponent: React.FC<{
  canConfirmBet: boolean
  selection: KambiSportsSelection
  selectedReward: KambiValidReward | null
}> = ({ canConfirmBet, selection, selectedReward }) => {
  const hasCheckedAutoSelectByDefaultRef = React.useRef(false)

  const setHasCheckedAutoSelectByDefault = React.useCallback(() => {
    hasCheckedAutoSelectByDefaultRef.current = true
  }, [])

  const rewardInfo = useGetKambiRewardsForSelection(selection)

  if (!selectedReward && canConfirmBet) return null

  if (canConfirmBet) {
    if (!selectedReward) return null

    return <SelectedReward selectedReward={selectedReward} />
  }

  const allValidRewards = rewardInfo
    ? [
        ...(rewardInfo.validRewards ?? []),
        ...(rewardInfo.validGroupRewards ?? []),
        //should not be displayed to the punter
        // ...(rewardInfo.implicitGroupRewards ?? []),
      ].map(validReward => validReward.reward)
    : []

  return (
    <AnimatedHeightToggle isExpanded={allValidRewards.length > 0}>
      <RewardSelection
        rewards={allValidRewards}
        selectedReward={selectedReward}
        setHasCheckedAutoSelectByDefault={setHasCheckedAutoSelectByDefault}
        hasCheckedAutoSelectByDefaultRef={hasCheckedAutoSelectByDefaultRef}
      />
    </AnimatedHeightToggle>
  )
}
